import { apiGet, apiPost, apiPatch, apiDelete } from "./client";
import type {
  Company,
  CompanyInput,
  CompanyEnrichmentPayload,
  Contact,
  ContactInput,
  MergedCompanyView,
} from "@career-ops-amplify/shared";

export interface ListCompaniesResponse {
  companies: MergedCompanyView[];
  total: number;
  scope?: string;
}

export interface ListGlobalCompaniesResponse {
  companies: Company[];
  total: number;
  scope?: string;
  filters?: { industry?: string; country?: string };
}

export interface CompanyFacets {
  industries: string[];
  countries: Array<{ slug: string; name: string; count: number }>;
}

/** List user's tracked companies (merged with global intel flags). */
export async function listMyCompanies(): Promise<ListCompaniesResponse> {
  return apiGet<ListCompaniesResponse>("/api/companies");
}

/** List seeded global directory only. */
export async function listGlobalCompanies(filters?: {
  industry?: string;
  country?: string;
}): Promise<ListGlobalCompaniesResponse> {
  return apiGet<ListGlobalCompaniesResponse>("/api/companies", {
    scope: "global",
    ...(filters?.industry ? { industry: filters.industry } : {}),
    ...(filters?.country ? { country: filters.country } : {}),
  });
}

/** Industry + HQ country facets for directory segmentation. */
export async function getCompanyFacets(): Promise<CompanyFacets> {
  return apiGet<CompanyFacets>("/api/companies/facets");
}

/** Fetch merged company detail (global + user shell + jobs + contacts). */
export async function getMyCompany(slug: string): Promise<MergedCompanyView> {
  return apiGet<MergedCompanyView>(`/api/my/companies/${slug}`);
}

/** Update user company shell fields. */
export async function updateMyCompany(
  slug: string,
  updates: Record<string, unknown>,
): Promise<void> {
  await apiPatch(`/api/my/companies/${slug}`, updates);
}

/** Ensure user shell exists and enable ATS scan for Find Jobs. */
export async function enableMyCompanyScan(
  slug: string,
  input: {
    name: string;
    ats: string;
    boardToken: string;
    careersUrl?: string;
    url?: string;
  },
): Promise<void> {
  await apiPost(`/api/my/companies/${slug}/enable-scan`, input);
}

/** Probe a public ATS board token before enabling scan. */
export async function probeAtsBoard(input: {
  ats: string;
  boardToken: string;
}): Promise<{ ok: boolean; status?: number; jobCount?: number; message?: string }> {
  return apiPost("/api/explore/probe-board", input);
}

/** Trigger Serper-grounded company enrichment (saved to user shell). */
export async function enrichMyCompany(slug: string): Promise<{
  message: string;
  research: string;
  enrichment: CompanyEnrichmentPayload;
  researchSections: Record<string, string>;
  lastResearchedAt: string;
}> {
  return apiPost(`/api/my/companies/${slug}/enrich`);
}

/** @deprecated Prefer enrichMyCompany — same pipeline via /research alias. */
export async function researchMyCompany(
  slug: string,
): Promise<{
  message: string;
  research: string;
  enrichment?: CompanyEnrichmentPayload;
  researchSections?: Record<string, string>;
  lastResearchedAt?: string;
}> {
  return enrichMyCompany(slug);
}

/** Create a global company (admin-style; rarely used from UI). */
export async function createCompany(input: CompanyInput): Promise<Company> {
  const res = await apiPost<{ company: Company }>("/api/companies", input);
  return res.company;
}

/** List contacts, optionally filtered / cursor-paginated. */
export async function listContacts(params?: {
  companySlug?: string;
  jobId?: string;
  role?: string;
  source?: string;
  search?: string;
  limit?: number;
  cursor?: string;
  offset?: number;
}): Promise<{
  contacts: Contact[];
  total?: number;
  limit?: number;
  offset?: number;
  hasMore?: boolean;
  nextCursor?: string;
}> {
  return apiGet("/api/contacts", params ?? {});
}

/** Create a contact. */
export async function createContact(
  input: ContactInput,
): Promise<Contact> {
  const res = await apiPost<{ contact: Contact }>("/api/contacts", input);
  return res.contact;
}

/** Update a contact. */
export async function updateContact(
  id: string,
  updates: Partial<ContactInput>,
): Promise<Contact> {
  const res = await apiPatch<{ contact: Contact }>(`/api/contacts/${id}`, updates);
  return res.contact;
}

/** Delete a contact. */
export async function deleteContact(id: string): Promise<void> {
  await apiDelete(`/api/contacts/${id}`);
}

/** Batch create contacts (contacto save, CSV import). */
export async function batchCreateContacts(
  contacts: ContactInput[],
): Promise<{ contacts: Contact[]; skipped: number }> {
  return apiPost("/api/contacts/batch", { contacts });
}

/** Log an outreach interaction on a contact. */
export async function logContactInteraction(
  id: string,
  input: { notes?: string; channel?: string },
): Promise<Contact> {
  const res = await apiPost<{ contact: Contact }>(`/api/contacts/${id}/interaction`, input);
  return res.contact;
}

/** @deprecated Use listMyCompanies */
export async function listCompanies(): Promise<ListCompaniesResponse> {
  return listMyCompanies();
}

/** @deprecated Use getMyCompany */
export async function getCompany(slug: string): Promise<MergedCompanyView> {
  return getMyCompany(slug);
}

/** @deprecated Use researchMyCompany */
export async function researchCompany(
  slug: string,
): Promise<{ message: string; research: string }> {
  return researchMyCompany(slug);
}

/** @deprecated Use updateMyCompany */
export async function updateCompany(
  slug: string,
  updates: Partial<Company>,
): Promise<void> {
  await updateMyCompany(slug, updates);
}
